import React, { useEffect } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import {
  Stack,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
} from "@mui/material";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useResetPasswordMutation } from "src/api/auth";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { LoadingButton } from "@mui/lab";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";

interface FormInputs {
  password: string;
  password_confirmation: string;
}

const Form: React.FC = () => {
  let { id } = useParams();
  const { t } = useTranslation("auth");
  const { t: T } = useTranslation("general");
  const [showPassword, setShowPassword] = React.useState(false);

  const schema = yup.object().shape({
    password: yup
      .string()
      .required(T("required"))
      .min(8, T("password8characters"))
      .matches(/^\S*$/, T("noSpacesAllowed")),
    password_confirmation: yup
      .string()
      .required(T("required"))
      .oneOf([yup.ref("password")], t("passwordsMustMatch")),
  });

  const navigate = useNavigate();

  const [notifications, setNotifications] = useRecoilState(notificationsState);

  const { mutate, status, isPending, error } = useResetPasswordMutation();

  const { control, handleSubmit, formState } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    mutate({
      ...data,
      token: id || "",
    });
  };

  useEffect(() => {
    if (status === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("passwordResetSuccessfully"),
        },
      ]);
      navigate("/login");
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors[0].value || t("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3} alignItems={"flex-start"}>
        <Stack gap={"10px"} width={"100%"}>
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <FormControl
                {...field}
                variant="outlined"
                error={!!formState.errors.password}
                size="small"
              >
                <InputLabel htmlFor="outlined-adornment-password">
                  {t("password")}
                </InputLabel>
                <OutlinedInput
                  id="reset-pass-password"
                  aria-describedby="my-helper-text"
                  type={showPassword ? "text" : "password"}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? (
                          <VisibilityOffOutlined fontSize="small" />
                        ) : (
                          <VisibilityOutlined fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                  label={t("password")}
                />
                <FormHelperText id="my-helper-text">
                  {formState.errors.password?.message}
                </FormHelperText>
              </FormControl>
            )}
          />

          {/* <Typography color="#667085" fontSize="14px">
           {t("useCharactersMix")}.
          </Typography> */}
        </Stack>

        <Controller
          name="password_confirmation"
          control={control}
          render={({ field }) => (
            <FormControl
              {...field}
              variant="outlined"
              error={!!formState.errors.password_confirmation}
              size="small"
              fullWidth
            >
              <InputLabel htmlFor="outlined-adornment-password">
                {t("confirmPassword")}
              </InputLabel>
              <OutlinedInput
                id="reset-pass-password"
                aria-describedby="my-helper-text"
                type={showPassword ? "text" : "password"}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? (
                        <VisibilityOffOutlined fontSize="small" />
                      ) : (
                        <VisibilityOutlined fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                label={t("confirmPassword")}
              />
              <FormHelperText id="my-helper-text">
                {formState.errors.password_confirmation?.message}
              </FormHelperText>
            </FormControl>
          )}
        />
        <LoadingButton
          loading={isPending}
          fullWidth
          type="submit"
          variant="contained"
        >
          {t("reset")}
        </LoadingButton>
      </Stack>
    </form>
  );
};

export default Form;
